import { DimensionX, InstallData, TechnologyMarketShareItem } from "./types";
import {
  ClusteredColumnChartDataItem,
  SeriesData,
} from "../../../../../../components/charts/ClusteredColumnChart/types";
import { abbreviateNumber } from "../../../../../../utils/numberUtils";

export const processChartData = (
  sourceData: TechnologyMarketShareItem[],
  selectedSegment: string,
  productNamesMapById: Record<number, string>
): ClusteredColumnChartDataItem[] => {
  return filterValidData(sourceData, selectedSegment)
    .map(({ dimensionX, installs }) => {
      const totalCompanyCount = (installs || []).reduce(
        (sum, install) => sum + (install.companyCount || 0),
        0
      );
      const cluster = createClusterItems(installs || [], productNamesMapById);
      return {
        category: generateCategoryLabel(dimensionX),
        cluster,
        totalCompanyCount,
      };
    })
    .filter(({ cluster }) => {
      // Filter out segments where all products have 0% penetration or no valid data
      return cluster.length > 0 && cluster.some((item) => item.value > 0);
    });
};

const filterValidData = (
  sourceData: TechnologyMarketShareItem[],
  selectedSegment: string
): TechnologyMarketShareItem[] => {
  return sourceData.filter(({ dimensionX, installs }) => {
    // Check if there are any valid installs with meaningful data
    if (
      !installs?.some(
        ({ id, penetrationPercent, companyCount }) =>
          id != null &&
          penetrationPercent > 0 &&
          Number.isFinite(penetrationPercent) &&
          companyCount > 0 &&
          Number.isFinite(companyCount)
      )
    ) {
      return false;
    }

    if (
      ["COUNTRY", "REGION", "HG_INDUSTRY", "INDUSTRY"].includes(selectedSegment)
    ) {
      return !!dimensionX?.name;
    }

    if (["REVENUE", "EMPLOYEES"].includes(selectedSegment)) {
      return dimensionX?.min !== undefined || dimensionX?.max !== undefined;
    }

    return true;
  });
};

const createClusterItems = (
  installs: InstallData[],
  productNamesMapById: Record<number, string>
): SeriesData[] => {
  return installs
    .filter(
      ({ id, penetrationPercent, companyCount }) =>
        id !== null &&
        productNamesMapById[id as number] &&
        (penetrationPercent || 0) >= 0 &&
        Number.isFinite(penetrationPercent || 0) &&
        (companyCount || 0) > 0 &&
        Number.isFinite(companyCount || 0)
    )
    .map(({ id, penetrationPercent, companyCount }) => ({
      name: productNamesMapById[id as number],
      value: penetrationPercent || 0,
      companyCount: companyCount || 0,
    }));
};

const generateCategoryLabel = (dimensionX: DimensionX): string => {
  if (!dimensionX) return "Unknown";
  if (dimensionX.name) return dimensionX.name;

  return generateRangeLabel(dimensionX);
};

const generateRangeLabel = (dimensionX: DimensionX): string => {
  const { min, max } = dimensionX;
  const hasMin = min != null;
  const hasMax = max != null;

  if (hasMin && hasMax)
    return `${abbreviateNumber(min)} to ${abbreviateNumber(max)}`;
  if (hasMin) return `${abbreviateNumber(min)} and above`;
  if (hasMax) return `Less than ${abbreviateNumber(max)}`;

  return "Unknown";
};
